import styled from '@emotion/styled'
import tokens from '@lacedltd/design-tokens'
import {
  Link,
  mediaQuery,
  generatePadding,
  generateMargin
} from '@lacedltd/laced-components'
import * as RadixNav from '@radix-ui/react-navigation-menu'

export const LogoWrapper = styled(RadixNav.Link)<{ christmasLogo: boolean }>(
  ({ christmasLogo }) => ({
    height: '100%',
    margin: generateMargin('spacing.6', 0),
    // christmas logo has more whitespace, so has to be scaled up 1.375x to ensure the text is the same size
    img: {
      height: christmasLogo ? '1.375rem' : '1rem',

      [mediaQuery.m.min]: {
        height: christmasLogo ? '1.71875rem' : '1.25rem'
      }
    }
  })
)

export const MenuIconButton = styled.button({
  color: tokens.color.airJordan.p,
  background: tokens.color.yeezy[0],
  padding: generatePadding('spacing.5', 'spacing.5', 'spacing.5', 0),
  marginRight: tokens.spacing[2],
  cursor: 'pointer',
  display: 'flex',

  [mediaQuery.l.min]: {
    marginRight: 0
  }
})

export const StyledLink = styled(Link)({
  svg: {
    padding: 0
  }
})

export const TopNavBar = styled(RadixNav.Root)({
  backgroundColor: tokens.color.yeezy[0],
  zIndex: tokens.zIndex.header,
  boxShadow: '0 1px 0px rgba(0,0,0,0.05)',

  // disabling the shadow when tapping links or icons on iOS safari
  '*': {
    WebkitTapHighlightColor: 'transparent'
  }
})

export const MenuInnerContainer = styled(RadixNav.List)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  backgroundColor: tokens.color.yeezy[0],
  justifyContent: 'space-between',
  padding: generatePadding(0, 'spacing.2', 0, 'spacing.5'),

  [mediaQuery.l.min]: {
    padding: generatePadding(0, 'spacing.5', 0, 'spacing.8')
  },

  [mediaQuery.xxl.min]: {
    padding: generatePadding(0, '15rem')
  }
})

export const ActionIcon = styled(RadixNav.Item)({
  display: 'flex',
  padding: generatePadding('spacing.5', 'spacing.4'),
  minWidth: 'fit-content',
  height: '74px',
  alignItems: 'center',

  // overriding the legacy styles of the site settings, can be deleted once we remove the old header
  '& > button': {
    padding: 0
  },

  svg: {
    float: 'left'
  },

  [mediaQuery.l.min]: {
    padding: generatePadding('spacing.5')
  }
})

export const SearchActionIcon = styled(ActionIcon)({
  [mediaQuery.l.max]: {
    display: 'none'
  }
})

export const StripeBadge = styled.div({
  display: 'flex',
  gap: tokens.spacing[5],
  margin: generateMargin('spacing.6', 0),

  p: {
    margin: 0
  }
})

export const NavbarBackdrop = styled.div({
  top: '100%',
  height: '100vh',
  position: 'absolute',
  left: 0,
  right: 0,
  background: `rgba(20, 21, 22, 0.5)`,
  zIndex: tokens.zIndex.below
})

export const NavPanel = styled.div({
  position: 'absolute',
  left: 0,
  top: '100%',
  bottom: 0,
  width: '100%',
  height: 'calc(100dvh - 100%)',
  transform: 'translateX(-100%)',
  visibility: 'hidden' as const,
  transition: 'transform 0.3s ease, visibility 0.3s ease',
  zIndex: tokens.zIndex.header,

  '&[data-state="open"]': {
    transform: 'translateX(0)',
    visibility: 'visible' as const
  },

  [mediaQuery.l.min]: {
    width: '50%'
  }
})

export const Backdrop = styled.div({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  height: '100vh',
  background: 'rgba(20, 21, 22, 0.5)',
  opacity: 0,
  visibility: 'hidden' as const,
  transition: 'opacity 0.3s ease, visibility 0.3s ease',
  zIndex: tokens.zIndex.below,

  '&[data-state="open"]': {
    opacity: 1,
    visibility: 'visible' as const
  }
})

export const RightBlock = styled.span({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flex: 1
})

export const LeftBlock = styled.span({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center'
})

export const LoadingContainer = styled.div<{ colour?: string }>(
  ({ colour }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacing[5],
    backgroundColor: colour ? colour : tokens.color.yeezy[0]
  })
)
